import { cn } from '../lib/cn'
import { ROTULO_ORIGEM, ROTULO_PRIORIDADE, ROTULO_RISCO, ROTULO_STATUS } from '../lib/glossario'
import type { NivelDeRisco, OrigemGmud, Prioridade, StatusGmud } from '../types/gmud'
import { IconeHistorico } from './Icones'

const CINZA = 'bg-slate-100 text-slate-700 ring-slate-200'

/**
 * Estado terminal do histórico importado (migração V10).
 *
 * Cinza-ardósia fechado, e não uma das cores do processo: as 1027 solicitações do sistema
 * anterior não estão em etapa nenhuma — não são "aprovadas", não estão "concluídas", não
 * aguardam nada. Pintá-las de verde faria a tela afirmar uma conformidade que nunca houve.
 * Distinto também do `CINZA` de rascunho: o anel mais escuro é o que separa "ainda não
 * começou" de "veio de outro sistema".
 */
const ARDOSIA = 'bg-slate-200 text-slate-900 ring-slate-400'

/**
 * Cor por estado.
 *
 * Os estados estão todos aqui, sem `default` silencioso: no sistema anterior só quatro
 * tinham cor e os outros seis caíam num cinza com "?", que ninguém percebeu porque a
 * tela não quebrava. O `Record` sobre `StatusGmud` é o que cobra a cor de todo estado novo,
 * `MIGRADO_V1` inclusive: sem a entrada, o build para aqui em vez de a tela cair no cinza.
 */
const COR_DO_STATUS: Record<StatusGmud, string> = {
  RASCUNHO: CINZA,
  SOLICITADA: 'bg-amber-50 text-amber-900 ring-amber-200',
  EM_AVALIACAO_CAB: 'bg-amber-50 text-amber-900 ring-amber-200',
  AGUARDANDO_AJUSTES: 'bg-orange-50 text-orange-900 ring-orange-200',
  APROVADA: 'bg-green-50 text-green-900 ring-green-200',
  RECUSADA: 'bg-red-50 text-red-900 ring-red-200',
  EM_EXECUCAO: 'bg-niteroi-50 text-niteroi-900 ring-niteroi-200',
  EM_VERIFICACAO: 'bg-niteroi-50 text-niteroi-900 ring-niteroi-200',
  CONCLUIDA: 'bg-green-100 text-green-900 ring-green-300',
  REVERTIDA_ROLLBACK: 'bg-purple-50 text-purple-900 ring-purple-200',
  // Cinza, e não vermelho: cancelar não é reprovar. Vermelho aqui faria a lista sugerir que a
  // solicitação foi recusada pelo comitê, que é outra coisa e conta em outro lugar.
  CANCELADA: CINZA,
  MIGRADO_V1: ARDOSIA,
}

const COR_DA_PRIORIDADE: Record<Prioridade, string> = {
  BAIXA: CINZA,
  MEDIA: 'bg-sky-50 text-sky-900 ring-sky-200',
  ALTA: 'bg-orange-50 text-orange-900 ring-orange-200',
  EMERGENCIAL: 'bg-red-50 text-red-900 ring-red-200',
}

const COR_DO_RISCO: Record<NivelDeRisco, string> = {
  BAIXO: CINZA,
  MODERADO: 'bg-sky-50 text-sky-900 ring-sky-200',
  ALTO: 'bg-orange-50 text-orange-900 ring-orange-200',
  CRITICO: 'bg-red-50 text-red-900 ring-red-200',
}

const BASE =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset'

/**
 * Estado da solicitação, com o rótulo em linguagem de gente.
 *
 * O glossário vem primeiro e o `rotulo` do servidor é só a rede de segurança — nesta ordem,
 * e não na inversa. A API manda `statusRotulo` junto de cada registro, e deixá-lo ganhar
 * faria a Regra nº 4 valer só onde ele não é enviado: hoje a lista e o cabeçalho recebem o
 * texto do servidor e a linha do tempo usa o glossário, então renomear "Aguardando pauta"
 * em `glossario.ts` mudaria uma das três telas e ninguém veria erro nenhum. Com o servidor
 * no fim da fila, um estado novo que o glossário ainda não conhece continua aparecendo
 * escrito por extenso em vez de virar um código cru.
 *
 * @param props o estado e o rótulo que o servidor mandou junto
 * @returns a etiqueta do estado
 */
export function EtiquetaDeStatus({ status, rotulo }: { status: StatusGmud; rotulo?: string }) {
  return (
    <span className={cn(BASE, COR_DO_STATUS[status] ?? CINZA)}>{ROTULO_STATUS[status] ?? rotulo ?? status}</span>
  )
}

/**
 * Procedência do registro — só aparece quando ele veio do sistema anterior.
 *
 * Não existe etiqueta para `V2`: marcar o que é normal não informa nada e vira ruído em toda
 * linha da lista. Quem tem de saltar aos olhos é o histórico, porque é ele que se confunde
 * com registro do processo novo — e a confusão, aqui, é o sistema afirmar 1025 aprovações de
 * comitê que não aconteceram. Isso dito, esta etiqueta é a segunda marca e não a única:
 * hoje todo registro importado está no estado `MIGRADO_V1`, que já tem etiqueta própria.
 * A marca boa de usar é esta: status é etapa e pode mudar, origem é fixada na importação.
 *
 * @param props a procedência que a API devolve em cada registro
 * @returns a marca de histórico, ou nada
 */
export function EtiquetaDeOrigem({ origem }: { origem: OrigemGmud }) {
  if (origem !== 'MIGRADO_V1') {
    return null
  }
  return (
    <span className={cn(BASE, ARDOSIA)}>
      <IconeHistorico className="h-3.5 w-3.5" />
      {ROTULO_ORIGEM.MIGRADO_V1}
    </span>
  )
}

/** Prioridade declarada na abertura. Mesma ordem de precedência da etiqueta de estado. */
export function EtiquetaDePrioridade({ prioridade, rotulo }: { prioridade: Prioridade; rotulo?: string }) {
  return (
    <span className={cn(BASE, COR_DA_PRIORIDADE[prioridade] ?? CINZA)}>
      {ROTULO_PRIORIDADE[prioridade] ?? rotulo ?? prioridade}
    </span>
  )
}

/**
 * Faixa de risco.
 *
 * Nula é o caso normal enquanto o comitê não avaliou — mostrar um travessão é honesto;
 * mostrar "Baixo" por omissão seria inventar uma avaliação que ninguém fez.
 */
export function EtiquetaDeRisco({ risco }: { risco: NivelDeRisco | null }) {
  if (!risco) {
    return <span className="text-sm text-slate-400">— não avaliado</span>
  }
  return <span className={cn(BASE, COR_DO_RISCO[risco] ?? CINZA)}>{ROTULO_RISCO[risco] ?? risco}</span>
}
